import PropTypes from "prop-types";
import React from "react";
import Button from "@material-ui/core/Button";
import { useTranslate } from "react-polyglot";
import { TextField } from "mui-rff";
import { makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useAuth } from "../../../Providers/Auth/AuthProvider.jsx";
import useTitle from "../../../hooks/useTitle.js";
import FlexboxVertical from "../../../SharedComponents/FlexboxVertical.jsx";
import ReactFinalForm from "../../../SharedComponents/ReactFinalForm.jsx";
import useCompanyPublicId from "../../../hooks/useCompanyPublicId.js";
import useFavicon from "../../../hooks/useFavicon.js";
import { getFirebaseApp } from "../../../Utils/firebase.js";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: 650,
  },
  title: {
    color: "#00213A",
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "27px",
  },
  form: {
    width: "100%",
    marginTop: 54,
    backgroundColor: "transparent",
  },
  fieldLabel: {
    marginBottom: 5,
    color: "#54606B",
    fontSize: 16,
    lineHeight: "22px",
  },
  emailField: {
    width: "100%",
    margin: 0,
    backgroundColor: "#FAFBFB",
    borderRadius: 15,
    "& .MuiOutlinedInput-root": {
      height: 52,
      color: "#00213A",
      backgroundColor: "#FAFBFB",
      borderRadius: 15,
      fontSize: 16,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#909293",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: "#909293",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E4E5",
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      height: 52,
      padding: 15,
      caretColor: "#CF2965",
    },
    "& .MuiFormHelperText-root": {
      margin: "2px 0 0",
    },
  },
  continueButton: {
    width: "100%",
    height: 52,
    marginTop: 60,
    borderRadius: 12,
    backgroundColor: "#CF2965",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#B9245A",
    },
    "& .MuiButton-label": {
      fontSize: 17,
      fontWeight: 400,
    },
  },
  separator: {
    display: "flex",
    width: "calc(100% + 10px)",
    margin: "16px -5px 0",
    alignItems: "center",
  },
  separatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#9E9E9E",
  },
  separatorText: {
    margin: "0 10px",
    color: "#9E9E9E",
    fontSize: 16,
    lineHeight: "22px",
  },
  googleButton: {
    alignSelf: "center",
    width: "auto",
    height: 40,
    minHeight: 40,
    marginTop: 16,
    padding: "0 16px 0 12px",
    color: "#1F1F1F",
    backgroundColor: "#FFFFFF",
    border: "1px solid #747775",
    borderRadius: 20,
    textTransform: "none",
    fontFamily: "Roboto, Arial, sans-serif",
    "&:hover": {
      backgroundColor: "#F8FAF8",
    },
    "& .MuiButton-label": {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: "20px",
    },
    "& .MuiButton-startIcon": {
      marginRight: 12,
      marginLeft: 0,
    },
  },
  googleLogo: {
    width: 18,
    height: 18,
  },
});

/** @returns {JSX.Element} */
const GoogleButton = ({ className }) => {
  const t = useTranslate();
  const { loginWithGoogle } = useAuth();
  const classes = useStyles();

  /** Обработчик клика по кнопке входа через Google */
  const handleClick = async () => {
    try {
      // Инициализируем/получаем общий экземпляр Firebase App
      const app = getFirebaseApp();
      const auth = getAuth(app);
      const provider = new GoogleAuthProvider();

      // Открываем стандартное Google-окно авторизации
      const result = await signInWithPopup(auth, provider);

      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential && credential.idToken;
      const user = result.user;
      const email =
        (user && user.email) || (user && user.providerData && user.providerData[0] && user.providerData[0].email);

      if (email && token) {
        // Вход через Google в бекенд Skillzrun
        await loginWithGoogle(email, token);
      } else {
        // Если по каким-то причинам не получили данные - логируем для отладки
        console.error("Google sign-in: email or idToken is missing", { hasEmail: !!email, hasToken: !!token });
      }
    } catch (e) {
      // Обработка ошибок авторизации через Google (отмена попапа, блокировка, сетевые ошибки и т.п.)
      console.error("Google sign-in error", e);
    }
  };

  return (
    <Button
      className={clsx(classes.googleButton, className)}
      size="large"
      type="button"
      variant="outlined"
      color="default"
      onClick={handleClick}
      startIcon={
        <img
          src="https://developers.google.com/identity/images/g-logo.png"
          alt="Google"
          className={classes.googleLogo}
        />
      }
    >
      {t("signInWithGoogle")}
    </Button>
  );
};

GoogleButton.propTypes = {
  className: PropTypes.string,
};

/** @param {String} className */
const CheckEmail = ({ className }) => {
  const { companyName, domain } = useCompanyPublicId();
  const t = useTranslate();
  const { checkEmail, company2 } = useAuth();
  const classes = useStyles();
  const rawCompanyColor = company2 && company2.colors && company2.colors.main;
  const companyColor = rawCompanyColor
    ? rawCompanyColor.charAt(0) === "#"
      ? rawCompanyColor
      : `#${rawCompanyColor}`
    : null;

  useTitle("title.login", { school: (company2 && company2.name) || companyName || domain });

  useFavicon();

  /** @param {String} email */
  const onSubmit = ({ email }) => {
    checkEmail(email);
  };

  return (
    <FlexboxVertical className={classes.root}>
      <Typography className={classes.title} align="center">
        {t("authCheckEmail.title")}
      </Typography>
      <ReactFinalForm onSubmit={onSubmit} formClassName={clsx(classes.form, className)}>
        <FlexboxVertical>
          <Typography className={classes.fieldLabel} component="label" htmlFor="auth-email">
            {t("authCheckEmail.emailCaption")}
          </Typography>
          <TextField
            className={classes.emailField}
            id="auth-email"
            name="email"
            type="email"
            placeholder="example@email.com"
            variant="outlined"
            fullWidth
            required
            inputProps={{
              "aria-label": t("authCheckEmail.emailCaption"),
              style: companyColor ? { caretColor: companyColor } : undefined,
            }}
          />
          <Button
            className={classes.continueButton}
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            style={companyColor ? { backgroundColor: companyColor } : undefined}
          >
            {t("authCheckEmail.continue")}
          </Button>
          <div className={classes.separator} aria-hidden="true">
            <span className={classes.separatorLine} />
            <span className={classes.separatorText}>{t("authCheckEmail.or")}</span>
            <span className={classes.separatorLine} />
          </div>
          <GoogleButton />
        </FlexboxVertical>
      </ReactFinalForm>
    </FlexboxVertical>
  );
};

CheckEmail.propTypes = {
  className: PropTypes.string,
};

export default CheckEmail;
